/**
 * Filters Context
 *
 * Provides global filter state management for audit logs.
 * Persists filter values to localStorage for user convenience.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AuditLogFilters } from '@/types/audit-log';

/**
 * Filters Context Value Interface
 * All values and methods exposed by FiltersContext
 */
interface FiltersContextValue {
  /** Current filter values */
  filters: AuditLogFilters;
  /** Update filter values */
  setFilters: (filters: AuditLogFilters) => void;
  /** Reset filters to default values, with optional overrides to preserve */
  resetFilters: (overrides?: Partial<AuditLogFilters>) => void;
  /** Update a single filter field */
  updateFilter: <K extends keyof AuditLogFilters>(key: K, value: AuditLogFilters[K]) => void;
}

/**
 * Build default date range: today back 30 days (ISO 8601 strings)
 */
function buildDefaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

/**
 * Default Filter Values
 * Dates are intentionally omitted — they are computed fresh at reset/load time
 * to avoid freezing the range at module initialization.
 */
const DEFAULT_FILTERS: AuditLogFilters = {
  page: 1,
  limit: 25,
  sortBy: 'occurredAt',
  sortOrder: 'desc',
  // dates will be added at reset/load time
};

/**
 * LocalStorage Key
 */
const STORAGE_KEY = 'audit-admin-filters';

/**
 * Load Filters from LocalStorage
 * Restores saved filter state or returns defaults
 */
function loadFiltersFromStorage(): AuditLogFilters {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as AuditLogFilters;
      // Always start at page 1 and reset dates to a fresh 30-day window on load
      const freshDates = buildDefaultDateRange();
      return { ...parsed, page: 1, ...freshDates };
    }
  } catch (error: unknown) {
    console.error('Failed to load filters from localStorage:', error);
  }
  return { ...DEFAULT_FILTERS, ...buildDefaultDateRange() };
}

/**
 * Save Filters to LocalStorage
 * Persists current filter state
 */
function saveFiltersToStorage(filters: AuditLogFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error: unknown) {
    console.error('Failed to save filters to localStorage:', error);
  }
}

/**
 * Filters Context
 */
const FiltersContext = createContext<FiltersContextValue | undefined>(undefined);

/**
 * Filters Provider Props
 */
interface FiltersProviderProps {
  children: ReactNode;
}

/**
 * Filters Provider Component
 *
 * Wraps components that need access to filter state.
 * Automatically persists filter changes to localStorage.
 */
export function FiltersProvider({ children }: FiltersProviderProps) {
  const [filters, setFiltersState] = useState<AuditLogFilters>(() => loadFiltersFromStorage());

  /**
   * Set Filters
   * Updates filter state and persists to storage
   */
  const setFilters = useCallback((newFilters: AuditLogFilters) => {
    setFiltersState(newFilters);
    saveFiltersToStorage(newFilters);
  }, []);

  /**
   * Reset Filters
   * Resets all filters to default values
   */
  const resetFilters = useCallback((overrides?: Partial<AuditLogFilters>) => {
    const newFilters = { ...DEFAULT_FILTERS, ...buildDefaultDateRange(), ...overrides };
    setFiltersState(newFilters);
    saveFiltersToStorage(newFilters);
  }, []);

  /**
   * Update Single Filter
   * Updates a single filter field while preserving others
   */
  const updateFilter = useCallback(
    <K extends keyof AuditLogFilters>(key: K, value: AuditLogFilters[K]) => {
      setFiltersState(prev => {
        const updated = { ...prev, [key]: value };
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    []
  );

  const contextValue: FiltersContextValue = {
    filters,
    setFilters,
    resetFilters,
    updateFilter,
  };

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
}

/**
 * Use Filters Hook
 * Custom hook to access filters context
 *
 * @returns Filters context value
 * @throws {Error} If used outside FiltersProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFilters(): FiltersContextValue {
  const context = useContext(FiltersContext);

  if (context === undefined) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }

  return context;
}
